import { useSelector } from 'react-redux';
import { CustomerType } from '@makemydeal/dr-shared-bff-types';
import { DeepLinks } from '@makemydeal/dr-sales-checkin-types';
import type { CustomerState } from './types';
import type { SalesCheckinRootState } from '../common/types/SalesCheckinRootState';
import { useDeepLinkUrlSelector } from '../deepLink/selector';
import { usePdfShareLinkUrlSelector } from '../pdfShareLink/selector';

const getActiveDealParticipants = (state: SalesCheckinRootState) => state.salesCheckin?.activeDeal?.details?.participants;

const getBuyer = (state: SalesCheckinRootState) => state.salesCheckin?.activeDeal?.details?.dealStructure?.participants?.buyer;

/**
 * Business-buyer flag plus its legal name, read from the same node. `customerType` is authoritative
 * on the top-level `participants[]` array (per participant), not on the denormalized
 * `dealStructure.participants.buyer` node (DealXG never populates it there), so match the buyer to
 * its participant by `commonConsumerId` and read that participant's `customerType` — and, since
 * `legalName` lives on that same participant record, its `legalName` too, rather than falling back
 * to the personal-name fields on `checkedInCustomer`. The flat `buyer.legalName` on the dealStructure
 * node is a fallback for when the participant match misses, or matches but carries no `legalName`
 * of its own.
 * @returns { isBusinessBuyer, legalName }
 */
export const useBusinessBuyer = (): { isBusinessBuyer: boolean; legalName: string } => {
    const buyer = useSelector(getBuyer);
    const participants = useSelector(getActiveDealParticipants);
    const buyerParticipant = participants?.find((participant) => participant.commonConsumerId === buyer?.commonConsumerId);

    return {
        isBusinessBuyer: buyerParticipant?.customerType === CustomerType.BUSINESS,
        legalName: buyerParticipant?.legalName || buyer?.legalName || ''
    };
};

/**
 * @returns boolean - true if business buyer, false otherwise
 */
export const useIsBusinessBuyer = (): boolean => useBusinessBuyer().isBusinessBuyer;

const getCustomerState = (state: SalesCheckinRootState): CustomerState => state.salesCheckin?.customer || {};

export const useCustomerSelector = (): CustomerState => {
    const customerState: CustomerState = useSelector(getCustomerState);
    return customerState;
};

/**
 * Plain (non-hook) selector for the checked-in customer's consumer id — for use outside React
 * components (e.g. Redux middleware), where hooks aren't available.
 */
export const getCheckedInConsumerId = (state: SalesCheckinRootState): string | undefined =>
    getCustomerState(state).checkedInCustomer?.consumerId;

export const useFormattedMessageSelector = (linkType: DeepLinks): string => {
    // Always call hooks first (cannot be conditional)
    const deepLinkUrl = useDeepLinkUrlSelector(linkType);
    // Deal PDF's URL comes from the share-click call (US2060917 item 2b/3a), not from
    // fetchDeepLinks/the deeplinkUrls Map creditApp/docUpload read — see item 5.
    const pdfShareLinkUrl = usePdfShareLinkUrlSelector();
    const { checkedInCustomer } = useCustomerSelector();

    const messageUrl = linkType === DeepLinks.DealPdf ? pdfShareLinkUrl : deepLinkUrl;

    // Return empty string for Invalid or default workflow (no message needed)
    if (linkType === DeepLinks.Invalid || !messageUrl) {
        return '';
    }

    // Get customer name
    const customerName = checkedInCustomer ? `${checkedInCustomer.firstName || ''} ${checkedInCustomer.lastName || ''}`.trim() : '';

    if (linkType === DeepLinks.DealPdf) {
        const greeting = checkedInCustomer?.firstName ? `${checkedInCustomer.firstName}, ` : '';
        return `${greeting}here's your personalized deal link: ${messageUrl}`;
    }

    // Build the message for TEXT/EMAIL
    const subject = linkType === DeepLinks.CreditApp ? 'Complete Your Credit Application' : 'Upload Your Documents';

    const body =
        linkType === DeepLinks.CreditApp
            ? `Hi ${customerName},\nTo move forward with financing, please complete your secure credit application using the link below:\n${messageUrl}\n\nThanks`
            : `Hi ${customerName},\nClick the link below to securely upload any documents needed to complete your deal—such as your driver's license, proof of insurance, or proof of income.\n${messageUrl}\n\nThanks`;

    return `${subject}\n\n${body}`;
};
